import { Injectable, inject } from '@angular/core';
import { SupabaseClient } from '@supabase/supabase-js';
import { AdminTopup } from '@app/model/admin-topup';
import { SUPABASE_CLIENT } from '@app/providers/supabase.provider';

@Injectable({
  providedIn: 'root'
})
export class AdminTopupService {

  private supabaseClient: SupabaseClient;

  constructor() {
    this.supabaseClient = inject(SUPABASE_CLIENT);
  }

  /**
   * 創建管理員加值記錄
   * @param adminTopup 加值記錄物件
   * @returns Supabase 回應
   */
  async createTopupRecord(adminTopup: AdminTopup) {
    const res = await this.supabaseClient.from('admin_topup').insert({
      admin_id: adminTopup.admin_id,
      target_user_id: adminTopup.target_user_id,
      amount: adminTopup.amount,
      note: adminTopup.note,
      topup_type: adminTopup.topup_type,
      created_at: new Date()
    });
    return res;
  }

  /**
   * 取得所有加值記錄
   * @returns 所有加值記錄
   */
  async getAllTopupRecords() {
    const res = await this.supabaseClient
      .from('admin_topup')
      .select('*')
      .order('created_at', { ascending: false });
    return res?.data;
  }

  /**
   * 根據管理員ID取得加值記錄
   * @param adminId 管理員ID
   * @returns 該管理員的所有加值記錄
   */
  async getTopupRecordsByAdminId(adminId: string) {
    const res = await this.supabaseClient
      .from('admin_topup')
      .select('*')
      .eq('admin_id', adminId)
      .order('created_at', { ascending: false });
    return res?.data;
  }

  /**
   * 根據目標用戶ID取得加值記錄
   * @param targetUserId 目標用戶ID
   * @returns 該用戶收到的所有加值記錄
   */
  async getTopupRecordsByTargetUserId(targetUserId: string) {
    const res = await this.supabaseClient
      .from('admin_topup')
      .select('*')
      .eq('target_user_id', targetUserId)
      .order('created_at', { ascending: false });
    return res?.data;
  }

  /**
   * 根據加值類型取得記錄
   * @param topupType 加值類型
   * @returns 該類型的所有加值記錄
   */
  async getTopupRecordsByType(topupType: string) {
    const res = await this.supabaseClient
      .from('admin_topup')
      .select('*')
      .eq('topup_type', topupType)
      .order('created_at', { ascending: false });
    return res?.data;
  }

  /**
   * 取得特定時間範圍內的加值記錄
   * @param startDate 開始日期
   * @param endDate 結束日期
   * @returns 該時間範圍內的加值記錄
   */
  async getTopupRecordsByDateRange(startDate: Date, endDate: Date) {
    const res = await this.supabaseClient
      .from('admin_topup')
      .select('*')
      .gte('created_at', startDate.toISOString())
      .lte('created_at', endDate.toISOString())
      .order('created_at', { ascending: false });
    return res?.data;
  }

  /**
   * 計算特定用戶的總加值金額
   * @param targetUserId 目標用戶ID
   * @returns 總加值金額
   */
  async getTotalTopupAmountByUser(targetUserId: string) {
    const records = await this.getTopupRecordsByTargetUserId(targetUserId);
    if (!records) return 0;
    return records.reduce((total, record) => total + record.amount, 0);
  }
}
